import { IonButton, IonFab, IonFabButton } from '@ionic/react';
import React, { useState } from 'react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { CareMapState } from './CareMapState';
import { ISupportGroup, ISupportGroupKey, SUPPORT_GROUP_KEYS } from './ICareMap';
import { HeaderPopover } from '../components/HeaderPopover';
import { GearIcon, CalendarPlusIcon, InnerhivePlusIcon } from '../theme/Icons';
import { MenuItem } from '../components/MenuItem';
import { WelcomePageState } from '../QuestionnaireWelcomePage';
import { useSubscription } from '@meraki-internal/state';
import { Divider } from '../components/Divider';
import { RevenueCatModel } from '../innerhive-plus/revenue-cat/RevenueCatModel';
import { InnerhivePlusDialogViewModel } from '../innerhive-plus/InnerhivePlusDialogViewModel';
import { ConnectCalendarViewModel } from '../appointments/ConnectCalendarViewModel';
import { DownloadCaremapMenuItem } from '../share-caremap/DownloadCaremapMenuItem';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';

export const CareMapConfigPopover: React.FC = () => {
    const [isMenuOpen, setMenuOpen] = useState(false);

    const careMapState = useInstance(CareMapState);
    const history = useInstance(HistoryViewModel);
    const mixpanel = useInstance(MixPanelEventEmitter);

    const allSupportGroups: ISupportGroupKey[] = SUPPORT_GROUP_KEYS.slice(0);

    const welcomePageState = useInstance(WelcomePageState);
    useSubscription(() => welcomePageState);

    const revenueCat = useInstance(RevenueCatModel);
    useSubscription(() => revenueCat);

    const dialogVM = useInstance(InnerhivePlusDialogViewModel);
    const connectCalDialogVM = useInstance(ConnectCalendarViewModel);

    // if we don't have any caremaps, then we can't show the menu for the active caremap
    if (!careMapState.hasActiveCareMap()){
        return null;
    }

    const missingSupportGroups = allSupportGroups.filter(supportGroupKey => {
        return !careMapState.getActiveCaremap()?.[supportGroupKey].isRelevant;
    });
    const isOnCaremap = history.getCurrentLocation().pathname.startsWith('/caremaps/');

    const showAddCategory = isOnCaremap && missingSupportGroups.length && careMapState.hasPermissionTo('put');

    const showConnectCalendar = careMapState.getActiveCaremap()?.essentialMapComplete;

    const toggleMenu = () => {
        setMenuOpen(oldState => {
            return !oldState;
        });
    };

    const onAdd = (supportGroupKey: ISupportGroupKey) => {
        setMenuOpen(false);

        const group = careMapState.getActiveCaremap()![supportGroupKey] as ISupportGroup;
        if (!group.isRelevant){
            careMapState.addOrUpdateSupportGroup({ supportGroupKey });
            mixpanel.track('Care Category Added', () => ({
                group: careMapState.getSupportGroupLabels(supportGroupKey).title
            }));
        }

        history.push(`/caremaps/${careMapState.getActiveCaremap()?.careMapId}/drawer/support-group/${supportGroupKey}`);
    };

    const hasInnerhivePlus = revenueCat.hasInnerhivePlus();

    const connectCalendar = async () => {
        setMenuOpen(false);

        if (hasInnerhivePlus) {
            connectCalDialogVM.open('care map settings');
        } else {
            const nowHasInnerhivePlus = await dialogVM.requirePurchase({ triggerSource: 'connect calendar' });
            if (nowHasInnerhivePlus) {
                connectCalDialogVM.open('care map settings');
            }
            // do nothing, user canceled paywall
        }
    };

    return (
        <>
            <IonFab
                onClick={toggleMenu}
                activated={isMenuOpen}
                id="caremap-config-button"
                style={{position: 'fixed', bottom: 20, right: 20, pointerEvents: 'all', zIndex: 5}}
            >
                <IonFabButton color="light" size="small">
                    <GearIcon color="primary" />
                </IonFabButton>
            </IonFab>

            <HeaderPopover
                isBottomFAB
                maxWidth="smaller"
                trigger="caremap-config-button"
                isOpen={isMenuOpen}
                onDidDismiss={() => setMenuOpen(false)}
                title={showAddCategory ? 'Add Care Category' : undefined}
                content={
                    showAddCategory ? missingSupportGroups.map(supportGroupKey => (
                        <div key={supportGroupKey}>
                            <IonButton
                                fill="outline"
                                expand="block"
                                onClick={() => onAdd(supportGroupKey)}
                                data-id={`add-support-group--${supportGroupKey}`}
                            >
                                {careMapState.getSupportGroupLabels(supportGroupKey).title}
                            </IonButton>
                        </div>
                    )) : undefined
                }
                lowerContent={
                    <>
                        {showConnectCalendar && (
                            <>
                                <MenuItem
                                    data-id="connect-calendar"
                                    onClick={connectCalendar}
                                    label={
                                        <>
                                            Connect Your Calendar
                                            {!hasInnerhivePlus && <span style={{marginLeft: 8}}><InnerhivePlusIcon color="secondary" /></span>}
                                        </>
                                    }
                                    Icon={CalendarPlusIcon}
                                />
                                <Divider />
                            </>
                        )}
                        <DownloadCaremapMenuItem />
                    </>
                }
            />
        </>
    );
};
